import React from 'react';

interface UserCardProps {
  id: number;
  name: string;
  email: string;
  avatarUrl?: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  totalRecordings: number;
  totalComments: number;
  workspaces: string;
  onView: (id: number) => void;
  onDelete: (id: number, email: string) => void;
}

const roleLabel = (isAdmin: boolean, isSuperAdmin: boolean) => {
  if (isSuperAdmin) return 'SuperAdmin';
  if (isAdmin) return 'Admin';
  return 'User';
};

const StatIcon: React.FC<{ color: string; path: string } > = ({ color, path }) => (
  <svg className={`mx-auto w-6 h-6 ${color}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d={path} />
  </svg>
);

// Simple icons: play circle, shield, chat bubble
const ICONS = {
  play: 'M8 5v14l11-7L8 5z',
  shield: 'M12 2l7 4v6c0 5-3.8 9.3-7 10-3.2-.7-7-5-7-10V6l7-4z',
  chat: 'M4 4h16v12H7l-3 3V4z',
};

const isValidHttpUrl = (url?: string | null) => !!url && /^https?:\/\//i.test(url) && url.length > 10;

const UserCard: React.FC<UserCardProps> = ({ id, name, email, avatarUrl, isAdmin, isSuperAdmin, totalRecordings, totalComments, workspaces, onView, onDelete }) => {
  const displayName = name && name.trim().length > 0 ? name : (email?.split('@')[0] || '(No name)');
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  return (
    <div
      className="bg-white rounded-lg shadow p-4 flex flex-col items-center text-center hover:shadow-md transition cursor-pointer relative"
      onClick={() => onView(id)}
      role="button"
      tabIndex={0}
    >
      {/* Delete action in top-right; does not trigger navigation */}
      <button
        className="absolute top-2 right-2 text-red-600 text-sm"
        onClick={(e) => { e.stopPropagation(); onDelete(id, email); }}
        aria-label="Delete user"
        title="Delete"
      >
        {/* simple trash can icon */}
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z" />
        </svg>
      </button>

      {/* Avatar */}
      {isValidHttpUrl(avatarUrl) ? (
        <img src={avatarUrl as string} alt={displayName} referrerPolicy="no-referrer" className="w-16 h-16 rounded-full object-cover bg-gray-100" />
      ) : (
        <div className="w-16 h-16 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
          {initials || '?'}
        </div>
      )}

      {/* Name and Email */}
      <div className="mt-3">
        <div className="font-semibold truncate max-w-[220px]" title={displayName}>{displayName}</div>
        <div className="text-sm text-gray-600 truncate max-w-[220px]" title={email}>{email}</div>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 w-full">
        <div>
          <StatIcon color="text-yellow-500" path={ICONS.play} />
          <div className="mt-1 text-xs text-gray-500">Recordings</div>
          <div className="font-medium">{totalRecordings}</div>
        </div>
        <div>
          <StatIcon color="text-gray-500" path={ICONS.shield} />
          <div className="mt-1 text-xs text-gray-500">Role</div>
          <div className="font-medium">{roleLabel(isAdmin, isSuperAdmin)}</div>
        </div>
        <div>
          <StatIcon color="text-blue-500" path={ICONS.chat} />
          <div className="mt-1 text-xs text-gray-500">Comments</div>
          <div className="font-medium">{totalComments}</div>
        </div>
      </div>

      <div className="mt-3 text-xs text-gray-500 truncate w-full" title={workspaces}>Workspaces: {workspaces || 'None'}</div>
    </div>
  );
};

export default UserCard;

